class GiftBoxDrawer extends HTMLElement {
  constructor() {
    super();

    this.handleExternalOpen = this.handleExternalOpen.bind(this);
    this.selectedOptions = {};
    this.previousScrollY = null;
  }

  connectedCallback() {
    this.overlay = this.querySelector('[data-gift-box-overlay]');
    this.panel = this.querySelector('[data-gift-box-panel]');
    this.closeButtons = this.querySelectorAll('[data-gift-box-close]');
    this.addButton = this.querySelector('[data-gift-box-add]');
    this.priceDisplay = this.querySelector('[data-gift-box-price-display]');
    this.errorEl = this.querySelector('[data-gift-box-error]');
    this.variantData = this.parseVariantData();
    this.cartDrawer = document.querySelector('cart-drawer');
    this.optionInputs = Array.from(this.querySelectorAll('.gift-box-drawer__option-values input[type="radio"]'));
    this.optionLabels = Array.from(this.querySelectorAll('[data-gift-box-option-label]'));
    this.ctaText = this.querySelector('[data-gift-box-cta-text]');
    this.mediaGallery = this.querySelector('[data-gift-box-gallery]');
    this.galleryId = this.dataset.galleryId;
    this.cartContext = null;
    this.discountEnabled = this.dataset.discountEnabled === 'true';

    this.defaultVariantId = Number(this.dataset.defaultVariantId);
    this.selectedVariant =
      this.variantData.find((variant) => variant.id === this.defaultVariantId) || this.variantData[0];

    if (this.selectedVariant) {
      this.selectedVariant.options.forEach((value, index) => {
        this.selectedOptions[index + 1] = value;
      });
    }

    this.attachEvents();
    this.updateOptionInputs();
    this.updateOptionLabels();
    this.updateDisplays();
    this.updateGallery();
  }

  disconnectedCallback() {
    document.removeEventListener('gift-box:open', this.handleExternalOpen);
  }

  attachEvents() {
    this.overlay?.addEventListener('click', () => this.close());
    this.closeButtons.forEach((btn) => btn.addEventListener('click', () => this.close()));
    this.addButton?.addEventListener('click', () => this.addToProduct());
    this.optionInputs.forEach((input) =>
      input.addEventListener('change', (event) => this.handleOptionChange(event)),
    );

    document.addEventListener('gift-box:open', this.handleExternalOpen);
  }

  parseVariantData() {
    try {
      const jsonNode = this.querySelector('[data-gift-box-variants]');
      if (!jsonNode) return [];
      return JSON.parse(jsonNode.textContent);
    } catch (error) {
      console.warn('Unable to parse gift box variant data', error);
      return [];
    }
  }

  handleExternalOpen(event) {
    const { button } = event.detail || {};
    if (button) {
      this.activeTrigger = button;
    } else {
      this.activeTrigger = document.querySelector('[data-gift-box-trigger]');
    }
    this.cartContext = event.detail?.cartContext || null;
    this.updateCtaLabel();
    this.closeCartDrawer();
    const requestedVariantId = Number(event.detail?.component?.variantId);
    const appliedVariant = this.applyVariantById(requestedVariantId);
    if (!appliedVariant) {
      this.syncSelectionFromWidget();
    }
    this.open();
  }

  syncSelectionFromWidget() {
    const widget = document.querySelector('[data-gift-box-widget]');
    if (!widget) return;
    const selectedId = Number(widget.dataset.selectedVariantId || widget.dataset.defaultVariantId || this.dataset.defaultVariantId);
    this.applyVariantById(selectedId);
  }

  applyVariantById(variantId) {
    if (!variantId || Number.isNaN(variantId)) return false;
    const variant = this.variantData.find((item) => item.id === variantId);
    if (!variant) return false;
    this.selectedVariant = variant;
    this.selectedOptions = {};
    variant.options.forEach((value, index) => {
      this.selectedOptions[index + 1] = value;
    });
    this.updateOptionInputs();
    this.updateOptionLabels();
    this.updateDisplays();
    this.updateGallery();
    return true;
  }

  open() {
    if (window.innerWidth <= 750) {
      this.previousScrollY = window.scrollY || 0;
    }
    document.body.classList.add('overflow-hidden');
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
  }

  close() {
    document.body.classList.remove('overflow-hidden');
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('is-open');
    if (this.previousScrollY !== null) {
      window.scrollTo({ top: this.previousScrollY, behavior: 'auto' });
      this.previousScrollY = null;
    }
    if (this.errorEl) this.errorEl.textContent = '';
    this.cartContext = null;
    this.updateCtaLabel();
  }

  handleOptionChange(event) {
    const input = event.currentTarget;
    if (input.disabled) return;
    const optionPosition = this.getOptionPosition(input);
    this.selectedOptions[optionPosition] = input.value;
    this.updateSelectedVariant();
  }

  getOptionPosition(input) {
    const optionAttr = input.getAttribute('data-option');
    if (optionAttr && optionAttr.includes('option')) {
      return Number(optionAttr.replace('option', ''));
    }
    const group = input.closest('[data-option-position]');
    return Number(group?.dataset.optionPosition);
  }

  updateSelectedVariant() {
    const match = this.variantData.find((variant) =>
      variant.options.every((value, index) => this.selectedOptions[index + 1] === value),
    );
    this.selectedVariant = match || null;
    this.updateOptionInputs();
    this.updateOptionLabels();
    this.updateDisplays();
    this.updateGallery();
  }

  updateOptionInputs() {
    if (!this.optionInputs?.length) return;
    this.optionInputs.forEach((input) => {
      const optionPosition = this.getOptionPosition(input);
      const shouldCheck = this.selectedOptions[optionPosition] === input.value;
      input.checked = shouldCheck;
      input.nextElementSibling?.classList.toggle('is-active', shouldCheck);
    });
  }

  updateOptionLabels() {
    if (!this.optionLabels?.length) return;
    this.optionLabels.forEach((label) => {
      const position = Number(label.dataset.giftBoxOptionLabel);
      const value = this.selectedOptions[position];
      if (value) {
        label.textContent = value;
      }
    });
  }

  addToProduct() {
    if (!this.selectedVariant) {
      this.setErrorMessage('Please select a valid option.');
      return;
    }

    if (!this.selectedVariant.available) {
      this.setErrorMessage('Selected option is unavailable.');
      return;
    }

    this.setErrorMessage('');
    const finalPrice = this.getEffectivePrice(this.selectedVariant.price);
    if (this.cartContext) {
      this.addGiftBoxToCart(finalPrice);
    } else {
      this.updateGiftWidget(finalPrice);
      this.close();
    }
  }

  setErrorMessage(message) {
    if (this.errorEl) {
      this.errorEl.textContent = message;
    }
  }

  updateGiftWidget(finalPriceCents) {
    const widget = document.querySelector('[data-gift-box-widget]');
    if (!widget) return;

    const baseTitle = widget.dataset.baseTitle || '';
    const titleEl = widget.querySelector('[data-gift-box-title]');
    const priceEl = widget.querySelector('[data-gift-box-price]');
    const chargeInput = widget.querySelector('[data-gift-box-charge-input]');
    const idInput = widget.querySelector('[data-gift-box-id-input]');
    const triggerBtn = this.activeTrigger || widget.querySelector('[data-gift-box-trigger]');

    if (titleEl) {
      titleEl.textContent = baseTitle;
    }

    if (priceEl) {
      priceEl.innerHTML = this.getPriceHtml(finalPriceCents, this.selectedVariant.price, { wrapOriginal: true });
    }

    if (chargeInput) {
      chargeInput.value = (finalPriceCents / 100).toString();
    }

    if (idInput) {
      idInput.value = this.selectedVariant.id;
    }

    widget.dataset.selectedVariantId = this.selectedVariant.id;

    if (triggerBtn) {
      triggerBtn.setAttribute('data-price', (finalPriceCents / 100).toFixed(2));
    }

    const giftWidget = document.querySelector('gift-widget');
    if (giftWidget && typeof giftWidget.addGiftProduct === 'function' && triggerBtn) {
      giftWidget.addGiftProduct(triggerBtn);
    } else if (giftWidget && triggerBtn) {
      giftWidget.handleBtn(triggerBtn);
      giftWidget.handleFields(triggerBtn);
      giftWidget.handlePrice();
    }
  }

  addGiftBoxToCart(finalPriceCents) {
    const cartItems = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
    if (!cartItems || !this.cartContext) return;

    const line = Number(this.cartContext.line) || 1;
    const quantity = Number(this.cartContext.quantity) || 1;
    const charges = (finalPriceCents / 100).toString();
    const surrogateTarget = {
      dataset: {
        isDiscount: this.cartContext.isDiscount || 'false',
        discount: this.cartContext.discount || '0',
      },
    };

    cartItems.updateComponent(
      cartItems,
      line,
      quantity,
      'gift-box',
      this.selectedVariant.id,
      charges,
      surrogateTarget,
    );

    this.close();
    this.openCartDrawer();
  }

  getEffectivePrice(priceInCents) {
    if (!this.discountEnabled) return priceInCents;
    const multiplier = Math.max(0, 100 - (parseFloat(this.dataset.discountPercent) || 0)) / 100;
    const discounted = Math.round(priceInCents * multiplier);
    return Math.max(0, discounted);
  }

  getPriceHtml(finalPrice, originalPrice, { wrapOriginal = false } = {}) {
    if (finalPrice <= 0) {
      const freeLabel = this.dataset.discountNote || 'FREE';
      const label = wrapOriginal ? `(${freeLabel})` : freeLabel;
      const compare = originalPrice > 0 ? ` <s>${this.formatCurrency(originalPrice)}</s>` : '';
      return `${label}${compare}`;
    }

    if (originalPrice > finalPrice) {
      return `${this.formatCurrency(finalPrice)} <s>${this.formatCurrency(originalPrice)}</s>`;
    }

    const formatted = this.formatCurrency(originalPrice);
    return wrapOriginal ? `(${formatted})` : formatted;
  }

  formatCurrency(cents) {
    const amount = cents / 100;
    const formatted = Number.isInteger(amount) ? amount.toFixed(0) : amount.toFixed(2);
    const currency = this.dataset.currencySymbol || '£';
    return `${currency}${formatted}`;
  }

  updateDisplays() {
    if (!this.selectedVariant) {
      if (this.priceDisplay) this.priceDisplay.textContent = 'Unavailable';
      if (this.addButton) this.addButton.disabled = true;
      return;
    }

    const variantPrice = this.getEffectivePrice(this.selectedVariant.price);
    const priceHtml = this.getPriceHtml(variantPrice, this.selectedVariant.price);
    if (this.priceDisplay) {
      this.priceDisplay.innerHTML = priceHtml;
    }
    if (this.addButton) {
      this.addButton.disabled = !this.selectedVariant.available;
    }
  }

  updateCtaLabel() {
    if (!this.ctaText) return;
    this.ctaText.textContent = this.cartContext ? 'Add to bag' : 'Add to product';
  }

  closeCartDrawer() {
    if (this.cartContext && this.cartDrawer) {
      this.cartDrawer.close(true);
    }
  }

  openCartDrawer() {
    if (this.cartDrawer) {
      this.cartDrawer.open();
    }
  }

  updateGallery() {
    if (!this.mediaGallery) return;

    const slides = Array.from(this.mediaGallery.querySelectorAll('.product__media-item'));
    if (!slides.length) return;

    const variantPrefixes = this.getVariantPrefixes();

    const matchesVariant = (slide) => {
      if (!variantPrefixes.length) return false;
      const slideKey = this.getSlideAltKey(slide);
      if (!slideKey) return false;
      return variantPrefixes.some((prefix) => slideKey.startsWith(prefix));
    };

    let visibleSlides = slides.filter(matchesVariant);
    if (!visibleSlides.length) {
      visibleSlides = slides.filter((slide) => !this.getSlideAltKey(slide));
    }
    if (!visibleSlides.length) {
      visibleSlides = slides;
    }

    slides.forEach((slide) => {
      const shouldShow = visibleSlides.includes(slide);
      slide.classList.toggle('is-hidden', !shouldShow);
      slide.classList.toggle('product__media-item--variant', !shouldShow);
    });

    const targetSlide = visibleSlides[0];
    if (!targetSlide) return;

    slides.forEach((slide) => slide.classList.remove('is-active'));
    targetSlide.classList.add('is-active');

    const mediaGalleryEl = this.mediaGallery.querySelector('media-gallery');
    const sliderComponent = this.mediaGallery.querySelector('slider-component');
    const targetMediaId = targetSlide.dataset.mediaId;

    if (mediaGalleryEl && targetMediaId) {
      mediaGalleryEl.setActiveMedia?.(targetMediaId, true);
    } else if (sliderComponent) {
      sliderComponent.resetPages?.();
      const sliderTrack = sliderComponent.querySelector('[id^="Slider-"]');
      if (sliderTrack) {
        sliderTrack.scrollTo({
          left: targetSlide.offsetLeft,
          behavior: 'smooth',
        });
      }
    }

    this.updateThumbnails(visibleSlides, targetSlide);
  }

  updateThumbnails(visibleSlides, targetSlide) {
    const thumbnailItems = Array.from(this.mediaGallery.querySelectorAll('.thumbnail-list__item'));
    if (!thumbnailItems.length) return;

    const visibleTargets = new Set(visibleSlides.map((slide) => slide.dataset.mediaId));
    thumbnailItems.forEach((item) => {
      const target = item.dataset.target;
      const shouldShow = visibleTargets.size ? visibleTargets.has(target) : true;
      item.classList.toggle('is-hidden', !shouldShow);
      item.classList.toggle('thumbnail-list_item--variant', !shouldShow);
      const button = item.querySelector('button');
      if (shouldShow && target === targetSlide.dataset.mediaId) {
        button?.setAttribute('aria-current', 'true');
      } else {
        button?.removeAttribute('aria-current');
      }
    });
  }

  getVariantPrefixes() {
    if (!this.selectedVariant) return [];
    const candidates = [
      this.selectedVariant.title,
      this.selectedVariant.option1,
      this.selectedVariant.option2,
      this.selectedVariant.option3,
      ...(this.selectedVariant.options || []),
    ];

    const prefixes = new Set();

    candidates.forEach((value) => {
      if (!value) return;
      const base = value.split('|')[0].trim();
      const normalized = this.normalizeKey(base);
      if (!normalized) return;

      prefixes.add(normalized);

      if (normalized.startsWith('size')) {
        const stripped = normalized.replace(/^size/, '');
        if (stripped) prefixes.add(stripped);
      } else {
        prefixes.add(`size${normalized}`);
      }
    });

    return Array.from(prefixes).filter(Boolean);
  }

  getSlideAltKey(slide) {
    if (!slide) return null;
    const sources = [];
    const dataAlt = slide.dataset?.mediaAlt;
    if (dataAlt) sources.push(dataAlt);

    const img = slide.querySelector('img');
    if (img) {
      const imgAlt = img.getAttribute('alt');
      if (imgAlt) sources.push(imgAlt);
      const imageSrc = img.currentSrc || img.src || img.getAttribute('data-src');
      if (imageSrc) {
        const filename = this.extractFilename(imageSrc);
        if (filename) sources.push(filename);
      }
    }

    const firstMatch = sources
      .map((value) => this.normalizeKey(value))
      .find((value) => value);
    return firstMatch || null;
  }

  normalizeKey(value) {
    if (!value || typeof value !== 'string') return null;
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  extractFilename(url) {
    try {
      const withoutQuery = url.split('?')[0];
      const segments = withoutQuery.split('/');
      const fileWithExt = segments.pop();
      if (!fileWithExt) return null;
      return fileWithExt.split('.').shift();
    } catch (error) {
      return null;
    }
  }
}

customElements.define('gift-box-drawer', GiftBoxDrawer);
